import { createSlice } from '@reduxjs/toolkit';
import { PURGE, REHYDRATE } from 'redux-persist';
import { datagridPageSizeOptions, shDatagridPageSizeOptions, SH_DATAGRID_DEFAULT_ROW_HEIGHT } from './DataGridGeneralOptions';

// const initialState = {
//   searchString: '',
//   columns: [
//     {
//       column: "BLAB",
//       visible: true,
//       pinned: true,
//     }
//   ],
//   sortedBy: [],
//   filter: {
//     groups: []
//   },
// };
const filterInitialState = {
  searchString: '',
  columns: [],
  sortedBy: [],
  sortModel: {},
  filter: {},
  overlayWidth: 0,
  // Row grouping (ShDataGrid3 only). `null` = flat rendering.
  // When set: { field: string, granularity: string | null }
  // Persisted into userLayoutSlice snapshots via useGridLayout.
  groupBy: null,
};

/**
 * Initial general state
 * Can save general data you want to apply acorss all views
 */
const initialState = {
  viewInstances: [],
  settings: {
    pageSize:datagridPageSizeOptions[0],
    // ShDataGrid3 has its own pageSize so it can go above 100 without
    // crashing ShDataGrid2 (MUI DataGrid can't handle >100 rows).
    pageSize3: shDatagridPageSizeOptions[0],
    rowHeight: SH_DATAGRID_DEFAULT_ROW_HEIGHT,
  }
};


const gridFilterSlice = createSlice({
  name: 'gridFilter',
  initialState: initialState,
  reducers: {
    updatePageSize: (state,action) => {
      state.settings.pageSize = action.payload;
    },
    updatePageSize3: (state,action) => {
      state.settings.pageSize3 = action.payload;
    },
    updateRowHeight: (state,action) => {
      // Accepts either a number (fixed px) or the string "auto". No validation on
      // purpose — the UI only offers a fixed set of options (see
      // shDatagridRowHeightOptions) and any future preset addition stays backward
      // compatible because the slice just stores whatever value it's handed.
      state.settings.rowHeight = action.payload;
    },
    createViewInstance: (state,action) => {
      // const {id, initialState} = action.payload;
      state.viewInstances.push({});
    },
    removeViewInstance: (state,action) => {
      state.viewInstances.splice(action.payload,1);
    },
    createFilterInstance: (state,action) => {
      const {index,id} = action.payload;
      state.viewInstances[index][id] = filterInitialState;
    },
    removeFilterInstance: (state,action) => {
        const { index, id } = action.payload;
        delete state.viewInstances[index][id];
    },

    updateOverlayWidth: (state,action) => {
      const { index, id,overlayWidth } = action.payload;
      state.viewInstances[index][id].overlayWidth = overlayWidth;
    },

    updateSort: (state,action) => {
      const { index, id,sortModel } = action.payload;

      if (!state.viewInstances[index]) {
        return;
      }

      if (!state.viewInstances[index][id]) {
        return;
      }

      state.viewInstances[index][id].sortModel = sortModel;
    },
    
    updateFilter: (state, action) => {
      const { index, id, filterKey, value } = action.payload;

      if (state.viewInstances[index]?.[id]) {
        // Update the filter object by key
        state.viewInstances[index][id].filter[filterKey] = value;
      }
    },

    clearFilterForInstance: (state, action) => {
      const { index, id } = action.payload;
      if (state.viewInstances[index]?.[id]) {
        state.viewInstances[index][id].filter = {};
      }
    },

    clearAllViewInstances: (state) => {
      state.viewInstances = [];
    },


    // createFilterInstance: (state, action) => {
    //   const { id, initialState } = action.payload;
    //   state.instances[id] = filterInitialState;
    //   state.instanceOrder.push(id); // Assuming instanceOrder is part of your initial state
    // },
    // removeFilterInstance: (state, action) => {
    //   const { id } = action.payload;
    //   delete state.instances[id];
    // },
    // removeLastFilterInstance: (state) => {
    //   if (state.instanceOrder.length > 0) {
    //     const lastId = state.instanceOrder.pop();
    //     delete state.instances[lastId];
    //   }
    // },
    setShGridColumns: (state, action) => {
      const { index,id, columns } = action.payload;
      if (state.viewInstances[index]?.[id]) {
        state.viewInstances[index][id].columns = columns;
      }
    },
    updateShGridColumn: (state, action) => {
      const { index,id, column, visible, pinned,width } = action.payload;
      const instance = state.viewInstances[index][id];
      if (instance) {
        const index = instance.columns.findIndex(col => col.column === column);
        if (index !== -1) {
          instance.columns[index] = {
            ...instance.columns[index],
            ...(visible !== undefined && { visible }),
            ...(pinned !== undefined && { pinned }),
            ...(width !== undefined && { width }),
          };
        }
      }
    },
    setShGridSortedBy: (state, action) => {
      const { index,id, sortedBy } = action.payload;
      if (state.viewInstances[index]?.[id]) {
        state.viewInstances[index][id].sortedBy = sortedBy;
      }
    },
    updateSearch: (state, action) => {
      const { index,id, searchString } = action.payload;
      if (state.viewInstances[index]?.[id]) {
        state.viewInstances[index][id].searchString = searchString;
      }
    },
    updateGroupBy: (state, action) => {
      // payload: { index, id, groupBy }
      // groupBy: null | { field: string, granularity: string | null }
      const { index, id, groupBy } = action.payload;
      if (state.viewInstances[index]?.[id]) {
        state.viewInstances[index][id].groupBy = groupBy ?? null;
      }
    },
   
    resetFilter: (state,action) => {
      state = initialState;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(PURGE, () => initialState)
      .addCase(REHYDRATE, (state, action) => {
        // Restore settings but clear viewInstances to prevent index growth on reload
        if (action.payload?.gridFilter) {
          return {
            ...state,
            settings: action.payload.gridFilter.settings ?? state.settings,
            viewInstances: []
          };
        }
        return state;
      })
  }
});

export const {
  updateOverlayWidth,
  createViewInstance,
  removeViewInstance,
  createFilterInstance,
  removeFilterInstance,
  updateSearch,
  resetFilter,
  resetSearchString,
  updateFilter,
  clearFilterForInstance,
  clearAllViewInstances,
  removeFilterGroup,
  removeFilterRule,
  options,
  setPreviousRoute,
  setActiveTabChanged,
  setShGridColumns,
  updateShGridColumn,
  setShGridSortedBy,
  updateSort,
  updatePageSize,
  updatePageSize3,
  updateRowHeight,
  updateGroupBy
} = gridFilterSlice.actions;

export default gridFilterSlice;